import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, MapPin, FileText, AlertCircle } from 'lucide-react';
import Card from '../../components/Card';
import Button from '../../components/Button';
import { apiService } from '../../services/apiService';
import { useAuth } from '../../contexts/AuthContext';
import {
  getEquipmentStatus,
  getStatusColor,
  getStatusLabel,
  getDaysUntil,
  renderCellDate,
  renderCell,
} from '../../utils/helpers';
import { EQUIPMENT_ICONS, EQUIPMENT_TYPE_LABELS } from '../../utils/constants';
import EquipmentDetailSummary from '../../components/EquipmentDetailSummary';

const EquipmentDetails = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { id } = useParams();
  const [equipment, setEquipment] = useState(null);
  const [maintenances, setMaintenances] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadEquipment();
  }, [id, user?.id]);

  const loadEquipment = async () => {
    try {
      setLoading(true);
      const data = await apiService.getEquipmentById(id);
      if (user?.role === 'client' && data && Number(data.clientId) !== Number(user.id)) {
        setEquipment(null);
        setMaintenances([]);
        return;
      }
      setEquipment(data);
      if (user?.id) {
        try {
          const all = await apiService.getMaintenances(user.id);
          setMaintenances(
            (all || []).filter((m) => Number(m.equipmentId) === Number(id))
          );
        } catch {
          setMaintenances([]);
        }
      }
    } catch (error) {
      console.error('Failed to load equipment:', error);
      console.error('Equipment ID:', id);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (!equipment) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Équipement non trouvé</p>
      </div>
    );
  }

  const status = getEquipmentStatus(equipment);
  const daysUntilMaintenance = equipment.nextMaintenanceDate ? getDaysUntil(equipment.nextMaintenanceDate) : null;
  const daysUntilExpiration = equipment.expirationDate ? getDaysUntil(equipment.expirationDate) : null;
  const backPath = equipment.siteId
    ? `/my-sites/${equipment.siteId}/equipment`
    : '/my-sites';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Button
          variant="outline"
          icon={ArrowLeft}
          onClick={() => navigate(backPath)}
          className="mb-4"
        >
          Retour
        </Button>
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-4">
            <div className="text-5xl">{EQUIPMENT_ICONS[equipment.type]}</div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{EQUIPMENT_TYPE_LABELS[equipment.type] || equipment.type}</h1>
              <p className="text-gray-600 mt-1">{equipment.serialNumber}</p>
            </div>
          </div>
          <span className={`px-4 py-2 rounded-full text-sm font-medium ${getStatusColor(status)}`}>
            {getStatusLabel(status)}
          </span>
        </div>
      </div>

      {/* Alerts */}
      {(daysUntilMaintenance !== null && daysUntilMaintenance <= 30) || (daysUntilExpiration !== null && daysUntilExpiration <= 30) ? (
        <Card>
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-6 h-6 text-orange-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 mb-2">Attention requise</h3>
              <ul className="space-y-1 text-sm text-gray-600">
                {daysUntilMaintenance !== null && daysUntilMaintenance <= 30 && (
                  <li>
                    • Maintenance prévue dans {daysUntilMaintenance} jour(s)
                  </li>
                )}
                {daysUntilExpiration !== null && daysUntilExpiration <= 30 && (
                  <li>
                    • Expiration dans {daysUntilExpiration} jour(s)
                  </li>
                )}
              </ul>
            </div>
          </div>
        </Card>
      ) : null}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Information */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Fiche équipement complète</h2>
            <EquipmentDetailSummary equipment={equipment} columns={2} dense excludeKeys={['clientName', 'clientCompany']} />
          </Card>

          {maintenances.length > 0 && (
            <Card>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Interventions enregistrées</h2>
              <ul className="divide-y divide-gray-100">
                {maintenances.map((m) => (
                  <li key={m.id} className="py-3 flex flex-wrap items-center justify-between gap-2 text-sm">
                    <span className="font-medium text-gray-900">{renderCell(m.type)}</span>
                    <span className="text-gray-600">
                      Prévue : {renderCellDate(m.scheduledDate, 'dd/MM/yyyy')}
                    </span>
                    <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-800 text-xs">
                      {renderCell(m.status)}
                    </span>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card>
            <h3 className="font-semibold text-gray-900 mb-4">Actions rapides</h3>
            <div className="space-y-3">
              <Button variant="primary" className="w-full" icon={MapPin} onClick={() => navigate('/my-plan')}>
                Voir sur le plan
              </Button>
              <Button variant="outline" className="w-full" icon={FileText} onClick={() => navigate('/my-reports')}>
                Mes rapports PDF
              </Button>
            </div>
          </Card>

          <Card>
            <h3 className="font-semibold text-gray-900 mb-4">Statut</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">État actuel</span>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(status)}`}>
                  {getStatusLabel(status)}
                </span>
              </div>
              {daysUntilMaintenance !== null && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Maintenance dans</span>
                  <span className="text-sm font-medium text-gray-900">
                    {daysUntilMaintenance} jour(s)
                  </span>
                </div>
              )}
              {daysUntilExpiration !== null && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Expire dans</span>
                  <span className="text-sm font-medium text-gray-900">
                    {daysUntilExpiration} jour(s)
                  </span>
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default EquipmentDetails;

